const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const GA_SCRIPT_ID = "ga4-gtag-js";

let isInitialized = false;
let hasTrackedInitialPageView = false;
let lastTrackedPath = null;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const hasMeasurementId = () =>
  typeof MEASUREMENT_ID === "string" && MEASUREMENT_ID.trim().length > 0;

const getPagePath = () => {
  if (!isBrowser()) {
    return "/";
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

/**
 * Loads gtag.js and configures GA4 once.
 * Safe no-op when the Measurement ID is missing or the browser is unavailable.
 */
export const initGoogleAnalytics = () => {
  if (!isBrowser() || !hasMeasurementId() || isInitialized) {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  // Avoid duplicate script tags if init is called more than once.
  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.async = true;
    script.id = GA_SCRIPT_ID;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onerror = () => {
      // Fail silently if the GA script cannot be loaded.
    };
    document.head.appendChild(script);
  }

  window.gtag("js", new Date());
  // Disable the automatic first page_view so SPA/hash tracking stays under our control.
  window.gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
  });

  isInitialized = true;
  return true;
};

/**
 * Tracks a page view. Deduplicates consecutive identical paths.
 */
export const trackPageView = (pagePath) => {
  if (!isBrowser() || !hasMeasurementId() || !isInitialized || typeof window.gtag !== "function") {
    return;
  }

  const path = pagePath || getPagePath();

  if (path === lastTrackedPath) {
    return;
  }

  lastTrackedPath = path;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: document.title,
    page_location: window.location.href,
  });
};

/**
 * Tracks a custom GA4 event.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [eventParams]
 *
 * @example
 * trackEvent("request_demo_clicked", { location: "homepage_hero" });
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (
    !isBrowser() ||
    !hasMeasurementId() ||
    !isInitialized ||
    typeof window.gtag !== "function" ||
    typeof eventName !== "string" ||
    !eventName.trim()
  ) {
    return;
  }

  window.gtag("event", eventName, eventParams);
};

/**
 * Tracks the initial page view only.
 * Hash-anchor navigation is tracked separately via navigation_clicked /
 * section_viewed so hash changes do not create duplicate page_view events.
 */
export const setupPageTracking = () => {
  if (!isBrowser() || !hasMeasurementId() || !isInitialized) {
    return;
  }

  if (!hasTrackedInitialPageView) {
    trackPageView(getPagePath());
    hasTrackedInitialPageView = true;
  }
};

/**
 * Convenience initializer used at the application entry point.
 */
export const initializeAnalytics = () => {
  const didInit = initGoogleAnalytics();
  if (didInit) {
    setupPageTracking();
  }
};
